import { Link } from "react-router-dom";
import { Star, MapPin, Clock, GraduationCap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatFee, planBadge, initials, resolveImg } from "@/lib/format";

export function CourseCard({ course }) {
  const inst = course.institution || {};
  const badge = planBadge(inst.plan, inst.featured);
  const isFree = inst.plan === "free";

  return (
    <article
      data-testid={`course-card-${course.id}`}
      className={`group rounded-xl border bg-white p-5 transition-all hover:shadow-lg md:p-6 ${
        inst.featured ? "border-sky-200 ring-1 ring-sky-100" : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {inst.logo ? (
            <img src={resolveImg(inst.logo)} alt={inst.name} className="h-12 w-12 rounded-md object-cover" />
          ) : (
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-50 font-heading font-bold text-blue-800">
              {initials(inst.name)}
            </span>
          )}
          <div>
            <p className="text-xs font-medium text-slate-400">
              <MapPin className="mr-1 inline h-3 w-3" />
              {[inst.city, inst.country].filter(Boolean).join(", ")}
            </p>
            <Link
              to={`/universities/${inst.slug}`}
              className="font-heading text-sm font-semibold text-slate-900 hover:text-blue-800"
            >
              {inst.name}
            </Link>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${badge.cls}`}>
          {badge.star && <Star className="h-3 w-3 fill-current" />}
          {badge.label}
        </span>
      </div>

      <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-slate-900">
        {course.course_name}
      </h3>

      {!isFree ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4 text-slate-400" />{course.duration}</span>
            <span className="font-semibold text-slate-900">{formatFee(course.fee, course.currency)}</span>
            <span className="inline-flex items-center gap-1"><GraduationCap className="h-4 w-4 text-slate-400" />{course.mode}</span>
          </div>
          {course.specialisations?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {course.specialisations.slice(0, 4).map((s) => (
                <span key={s} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">{s}</span>
              ))}
            </div>
          )}
          {course.description && (
            <p className="mt-3 line-clamp-2 text-sm text-slate-500">{course.description}</p>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-slate-500">{course.course_name} available. View university for details.</p>
      )}

      <div className="mt-5 flex items-center justify-between">
        <Button asChild size="sm" className="bg-blue-800 hover:bg-blue-900" data-testid={`view-course-${course.id}`}>
          <Link to={`/courses/${course.slug}/${inst.slug}`}>
            {isFree ? "View University" : "View Course"} <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
        {course.category && <span className="text-xs text-slate-400">{course.category}</span>}
      </div>
    </article>
  );
}
